/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { GUILD_MESSAGE, NOTIFICATION_MESSAGE } from 'i18n/constants'
import { notification } from 'antd'

import ModalManagerMember from 'features/Guild/components/ModalManagerMember'
import { RequestLoginModal, RaceTable } from 'features/Race/components'
import { useAppSelector, useDebounce, useToggle, useUpdateEffect } from 'hooks'
import { Career, defaultRegionFilters, MyHorseListParams } from 'models'
import { SearchInput } from 'shared'
import { links } from 'apps'
import { STRING, NUMBER, STRING_REGION } from 'apps/constants'
import { guildHorseBlacklistColumns } from 'utils/columns'
import constants from 'apps/constants'
import guildApi from 'apis/guildApi'
import { handleAsyncRequest, shortenUserName } from 'utils/helper'
import ManagementHorseBlacklistStyled from './styled'
import SelectCustom from 'shared/SelectCustom'

const myHorseListParams: MyHorseListParams = {
  limit: NUMBER.TWENTY,
  page: NUMBER.ONE
}

export default function ManagementHorseBlacklist() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [api, contextHolder] = notification.useNotification()
  const { profile } = useAppSelector(state => state)
  const [params, setParams] = useState<MyHorseListParams>(myHorseListParams)
  const [searchValue, setSearchValue] = useState<string>('')
  const debounceSearchValue = useDebounce<string>(searchValue, constants.DEBOUNCE_TIME)
  const [isRequestLoginModalOpen, toggleIsRequestLoginModalOpen] = useToggle(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [guildHorseBlacklist, setGuildHorseBlacklist] = useState<any>([])
  const [total, setTotal] = useState()
  const [showModal, setShowModal] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const horseIdRef = useRef<any>(null)
  const [regionSelect, setRegionSelect] = useState('Region')

  const memoizedResultListSelectionColumns = useMemo(() => {
    return guildHorseBlacklistColumns
  }, [profile])

  useEffect(() => {
    if (profile.guild_id !== NUMBER.ZERO) {
      getListGuildUser()
    }
  }, [params, profile])

  const getListGuildUser = async () => {
    setLoading(true)
    const [error, result]: any = await handleAsyncRequest(guildApi.getListGuildHorseBlacklist(profile.guild_id, { ...params }))
    
    if (error) {
      openNotification(STRING.ERROR, error.message || error.errors[NUMBER.ZERO]?.message)
    }

    if (result) {
      const formatData = result.data.records.map((record: any) => {        
        return {
          ...record,
          member: shortenUserName(record?.user_name),
          racing_class_name: record?.racing_class,
          winRate: handleCareer(record?.career),
          horse_name: record?.name,
          guildGender: record?.gender,
          guildRegion: record?.region,
          horse: {
            is_new_record: record?.is_new_record
          }
        }
      })
      setGuildHorseBlacklist(params.page > NUMBER.ONE ? [...guildHorseBlacklist, ...formatData] : formatData)
      setTotal(result.data?.total || NUMBER.ZERO)
    }

    setLoading(false)
  }

  useUpdateEffect(() => {
    setParams({ ...params, page: NUMBER.ONE, search: debounceSearchValue })
  }, [debounceSearchValue])

  const handleSearchValueChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
  }

  const onClickCancelHorse = (value: any) => {
    horseIdRef.current = value?.id
    
    setShowModal(!showModal)
  }

  const handleRowItemClick = (horseId: any) => {
    const filterHorse = guildHorseBlacklist?.filter((horse: any) => {
      return horse.id === horseId
    })
    if (filterHorse?.length) {
      navigate(links.guild.horseBlacklistDetail(profile.guild_id, horseId, NUMBER.THREE))
    }
  }

  const openNotification = (message: string, description: string) => {
    notification.config({
      duration: 4
    })

    api.info({
      message,
      description,
      placement: 'bottomRight',
      className: message === STRING.ERROR ? 'ant-custom-error' : 'ant-custom-success'
    })
  }

  const handleCareer = (career: Career) => {
    const totalRace = career?.total_number_of_races
    const firstClass = career?.first_count
    const secondClass = career?.second_count
    const thirdClass = career?.third_count
    const tltClass = (firstClass + secondClass + thirdClass) / totalRace
    const newTltClass = isNaN(tltClass) ? 0 : tltClass * 100
    const winRateResult = `${Number.isInteger((firstClass / totalRace) * 100)
      ? (firstClass / totalRace) * 100
      : ((firstClass / totalRace) * 100).toFixed(2)
      }% - ${Number.isInteger(newTltClass) ? newTltClass : newTltClass.toFixed(2)}%`
    let valueDisplay = ''
    if (totalRace === 0) {
      valueDisplay = '---'
    }
    if (totalRace > 0 && firstClass === 0 && secondClass === 0 && thirdClass === 0) {
      valueDisplay = '0.00% - 0.00%'
    }
    if (totalRace !== 0 && (firstClass !== 0 || secondClass !== 0 || thirdClass !== 0)) {
      valueDisplay = winRateResult
    }
    return valueDisplay
  }

  const _handleToggleModal = () => {
    setShowModal(!showModal)
  }

  const _handleUpgrade = async () => {
    setIsLoading(true)
    const [error, result]: any = await handleAsyncRequest(guildApi.putCancelHorseBlacklist(profile.guild_id, horseIdRef.current))
    setIsLoading(false)
    if (error) {
      openNotification(STRING.ERROR, error.message || error.errors[NUMBER.ZERO]?.message)
    }

    if (result) {
      getListGuildUser()
      _handleToggleModal()
      openNotification(STRING.SUCCESS, t(`${GUILD_MESSAGE}.message.cancel_success`))
    }
  }

  const handleRegionFilterClicked = (item: string) => {
    if (item?.toUpperCase() === STRING_REGION.SEOUL || item?.toUpperCase() === STRING_REGION.TOKYO) {
      setParams({
        ...params,
        page: NUMBER.ONE,
        region: item?.toUpperCase()
      })
    } else {
      setParams({
        ...params,
        page: NUMBER.ONE,
        region: ''
      })
    }
    setRegionSelect(item)
  }

  return (
    <ManagementHorseBlacklistStyled>
      {contextHolder}
      <div className='search-box'>
        <SearchInput
          searchValue={searchValue}
          handleSearchValueChanged={handleSearchValueChanged}
          customClass='search-horse-input'
          placeholder={t(`${GUILD_MESSAGE}.search_horse`)}
        />

        {/* region */}
        <SelectCustom
          dataSelect={defaultRegionFilters}
          nameSelect={regionSelect}
          onSelected={handleRegionFilterClicked}
          customClass='select-custom'
        />
      </div>

      <div className='members'>
        <RaceTable
          columns={memoizedResultListSelectionColumns}
          data={guildHorseBlacklist ?? []}
          raisePage={setParams}
          loader={!loading}
          params={params}
          isRowClickable
          checkLoadingPage={false}
          totalRace={total}
          currentUser={profile}
          noDataText={t(`${NOTIFICATION_MESSAGE}.dataNotFound`)}
          onClickOneCell={(id) => onClickCancelHorse(id)}
          onRowClick={handleRowItemClick}
          isShowVipIcon={true}
        />
      </div>

      {showModal && (
        <ModalManagerMember
          title={t(`${GUILD_MESSAGE}.title_modal_cancel_horse`)}
          message={
            <p>
              {t(`${GUILD_MESSAGE}.content_modal_cancel_horse`)}<br />
              {t(`${GUILD_MESSAGE}.content_modal_cancel_horse2`)}
            </p>}
          onClose={_handleToggleModal}
          onConfirm={_handleUpgrade}
          loading={isLoading}
          titleColor="rgb(78, 240, 118)"
          width={560}
          heigth={240}
        />
      )}

      {isRequestLoginModalOpen && <RequestLoginModal toggleIsModalOpen={toggleIsRequestLoginModalOpen} />}
    </ManagementHorseBlacklistStyled>
  )
}
